import React, { Suspense, useState, useEffect, useCallback, useRef, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import 'bootstrap/dist/css/bootstrap.min.css';
import "./css/App.css";

import { logout } from "./actions/auth-action";
import { clearMessage } from "./actions/message-action";
import { history } from "./helpers/history";
import EventBus from "./common/EventBus";
import i18n from './i18n';
import LocaleContext from './LocaleContext';
import Loading from './components/Loading/Loading';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from 'react-bootstrap';
import styled from 'styled-components';

import Layout from './Layout';
import exportAsImage from "./utils/exportToImage";
import { WEB_AUTH_URL } from "./configurations/paths.config";
import MainAppContext from "./MainAppContext";
import { __ProfileName } from "./common/Dictionaries";

const AppContainer = styled.section`
--transition-settings-1: 0.2s ease;
--box-shadow-6dp: 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12), 0 3px 5px -1px rgba(0,0,0,0.20);
--box-shadow-9dp: 0 9px 12px 1px rgba(0,0,0,0.14), 0 3px 16px 2px rgba(0,0,0,0.12), 0 5px 6px -3px rgba(0,0,0,0.20);
--light-primary-color: #BBDEFB;
--on-light-primary-color: #000;
--primary-accent: #009688;
--on-primary-accent: #FFF;
--primary-color: #333;
--on-primary-color: #FFF;
--primary-surface: #FFF;
--on-primary-surface: #000;
--icon-container-size: 2rem;
--sidebar-z-index: 400;
--header-z-index: 500;
--sidebar-max-width: 11vw;
`;

const appTheme = {
  fonts: {
    large: { fontFamily: 'segoeui' },
    medium: { fontFamily: 'segoeui' },
    mediumPlus: { fontFamily: 'segoeui' },
    mega: { fontFamily: 'segoeui' },
    small: { fontFamily: 'segoeui' },
    smallPlus: { fontFamily: 'segoeui' },
    superLarge: { fontFamily: 'segoeui' },
    tiny: { fontFamily: 'segoeui' },
    xLarge: { fontFamily: 'segoeui' },
    xSmall: { fontFamily: 'segoeui' },
    xxLarge: { fontFamily: 'segoeui' }
  }
};

const getLanguageFromAuth = (currentAuth) => {
  if (currentAuth !== null && currentAuth !== undefined) {
    if (currentAuth.locale === "English") {
      return "en";
    }

    if (currentAuth.locale === "Spanish") {
      return "es";
    }
  }

  return "es";
};

const App = () => {
  const dispatch = useDispatch();
  const exportRef = useRef();

  const { user: currentAuth } = useSelector((state) => state.auth);

  const profileList = useMemo(() => {
    if (currentAuth !== null && currentAuth !== undefined && Array.isArray(currentAuth.profile)) {
      return currentAuth.profile;
    }

    return [];
  }, [currentAuth]);

  const language = getLanguageFromAuth(currentAuth);
  const [locale, setLocale] = useState(language);

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }

    setLocale(language);
  }, [language]);

  useEffect(() => {
    const onLanguageChanged = (lng) => {
      setLocale(lng);
    };

    i18n.on('languageChanged', onLanguageChanged);

    return () => {
      if (i18n.off) {
        i18n.off('languageChanged', onLanguageChanged);
      }
      else if (i18n.removeListener) {
        i18n.removeListener('languageChanged', onLanguageChanged);
      }
    };
  }, []);

  useEffect(() => {
    const unlisten = history.listen(() => {
      dispatch(clearMessage());
    });

    return () => {
      if (unlisten) {
        unlisten();
      }
    };
  }, [dispatch]);

  const logOut = useCallback(() => {
    const userName = currentAuth !== null && currentAuth !== undefined ? currentAuth.userName : "";
    return dispatch(logout(WEB_AUTH_URL, false, userName));
  }, [dispatch, currentAuth]);

  useEffect(() => {
    const onLogout = () => {
      logOut();
    };

    EventBus.on("logout", onLogout);

    return () => {
      EventBus.remove("logout");
    };
  }, [logOut]);

  const contextStructure = useMemo(() => {
    return {
      username: currentAuth !== null && currentAuth !== undefined ? currentAuth.userName : "",
      clientName: currentAuth !== null && currentAuth !== undefined ? currentAuth.clientName : "",
      clientId: currentAuth !== null && currentAuth !== undefined ? currentAuth.clientId : "",
      onlyObserver: profileList.indexOf("Observador") !== -1,
    };
  }, [currentAuth, profileList]);

  const layoutProfile = useMemo(() => {
    const isSAdmin = profileList.indexOf(__ProfileName.sAdmin) !== -1;
    const isAdmin = profileList.indexOf(__ProfileName.Admin) !== -1;

    return {
      isSAdmin: isSAdmin,
      isAdmin: isAdmin,
      isUser: !isSAdmin && !isAdmin
    };
  }, [profileList]);

  const localeContextValue = useMemo(() => {
    return {
      locale: locale,
      setLocale: setLocale
    };
  }, [locale]);

  const handleExportAsImage = useCallback(() => {
    if (exportRef.current) {
      exportAsImage(exportRef.current, "test");
    }
  }, []);

  return (
    <MainAppContext.Provider value={ contextStructure }>
      <LocaleContext.Provider value={ localeContextValue }>
        <Suspense fallback={<Loading />}>
          <Helmet htmlAttributes={{
            lang: locale,
            dir: 'ltr'
          }}>
          </Helmet>
          <ThemeProvider theme={appTheme} dir={'ltr'}>
            <div ref={exportRef}>
              <AppContainer>
                <Layout
                  exportAsImage2={handleExportAsImage}
                  profile={layoutProfile}
                />
              </AppContainer>
            </div>
          </ThemeProvider>
        </Suspense>
      </LocaleContext.Provider>
    </MainAppContext.Provider>
  );
};

export default App;
